"use client"
import CountUp from "./animations/CountUp"
import { useEffect, useState } from "react"
import { Search, MessageCircle, Radar, ArrowRight } from "lucide-react"
import Config from "../../Config"
import Reveal from "../../components/Reveal"

export default function StatsOverviewSection() {
  const [stats, setStats] = useState([
    { title: "Posts Analyzed", value: "-", icon: Search, description: "Public posts scanned for misinformation" },
    { title: "Responses Generated", value: "-", icon: MessageCircle, description: "Fact-based replies created" },
    { title: "Live Monitors", value: "-", icon: Radar, description: "Detection streams running now" },
  ])

  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetch(`${Config.API_BASE_URL}${Config.STATS_ROUTE}`)
      .then((res) => res.json())
      .then((data) => {
        setStats([
          {
            title: "Posts Analyzed",
            value: formatNumber(data.totalSentimentChecks),
            icon: Search,
            description: "Public posts scanned for misinformation",
          },
          {
            title: "Responses Generated",
            value: formatNumber(data.repliesGenerated),
            icon: MessageCircle,
            description: "Fact-based replies created",
          },
          {
            title: "Live Monitors",
            value: formatNumber(data.activeBotCount),
            icon: Radar,
            description: "Detection streams running now",
          },
        ])
        setIsLoading(false)
      })
      .catch((err) => {
        console.error("Failed to fetch stats:", err)
        setIsLoading(false)
      })
  }, [])

  const formatNumber = (num) => {
    return typeof num === "number" ? num.toLocaleString() : "-"
  }

  return (
    <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-8">
      {/* Header */}
      <Reveal className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-[#004aad] mb-4 tracking-[-0.01em]">Real-Time Impact</h2>
        <p className="text-[#004aad]/80 text-lg max-w-3xl mx-auto">
          Hasbara.ai works around the clock - analyzing public posts on X, detecting anti-Israel
          misinformation, and generating fact-based responses in real time.
        </p>
      </Reveal>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-12">
        {stats.map((stat, idx) => {
          const Icon = stat.icon
          return (
            <Reveal key={idx} delay={idx * 70} className="h-full">
              <div
                data-ai-surface="card"
                className="h-full bg-white border border-[#004aad]/20 rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,74,173,0.08)] transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] hover:shadow-[0_14px_34px_rgba(0,74,173,0.16)] hover:border-[#004aad]/40 hover:-translate-y-1"
              >
                <div className="flex items-center mb-3">
                  <div className="bg-[#004aad]/10 p-3 rounded-lg mr-3">
                    <Icon className="w-6 h-6 text-[#004aad]" />
                  </div>
                  <h3 className="text-[#004aad] font-semibold text-lg">{stat.title}</h3>
                </div>
                <div className="flex flex-col">
                {isLoading ? (
                  <p className="text-4xl font-bold text-[#000000] tabular-nums animate-pulse">-</p>
                ) : (
                  <CountUp
                    from={0}
                    to={typeof stat.value === "string" ? parseInt(stat.value.replace(/,/g, '')) : stat.value}
                    separator=","
                    direction="up"
                    duration={1}
                    className="text-4xl font-bold text-[#000000] tabular-nums"
                  />
                )}
                  <p className="text-[#004aad]/85 text-sm mt-1">{stat.description}</p>
                </div>
              </div>
            </Reveal>
          )
        })}
      </div>

      {/* Call to Action */}
      <Reveal className="text-center">
        <p className="text-[#004aad] text-xl font-medium mb-4">See it for yourself</p>

        <a
          href="#get-started"
          className="press inline-flex items-center gap-2 bg-[#004aad] text-white px-6 py-3 rounded-full font-medium transition-[background-color,box-shadow] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-[#003c8a] hover:shadow-lg"
        >
          Try the response generator
          <ArrowRight className="w-4 h-4" />
        </a>
      </Reveal>
    </section>
  )
}
